import { createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { PowerSyncDrizzleDB, Todo } from "@listto/powersync";
import {
  createTodoMutation,
  type createTodoMutation as CreateTodoMutationType,
  deleteTodoMutation,
  purgeTodoMutation,
  restoreTodoMutation,
  toggleTodoMutation,
} from "@listto/shared";
import { useUndo } from "@/lib/undo";
import { useI18n } from "@/providers/i18n-provider";

// Index of the optional extra props argument in createTodoMutation
const MUTATION_OPTIONS_INDEX = 3;

export const EXIT_DURATION_MS = 200;
const NEW_ITEM_ANIMATION_DURATION_MS = 500;

type CreateTodoOpts = Parameters<typeof CreateTodoMutationType>[typeof MUTATION_OPTIONS_INDEX];

interface UseTodoMutationsOptions {
  userId: () => string | undefined;
  listId: () => string;
  defaultAddTodoProps?: () => CreateTodoOpts | undefined;
  drizzle: () => PowerSyncDrizzleDB | null | undefined;
  todoInput: () => HTMLInputElement | null;
  currentListTodos: () => Todo[] | undefined;
  /**
   * Deterministic natural-language quick-add resolver. Given the raw input it
   * returns the cleaned task title plus extra creation options (due date, list)
   * derived from inline tokens. Returns `null` when nothing was detected, in
   * which case the raw trimmed text is used as-is.
   */
  resolveQuickAdd?: (raw: string) => { text: string; opts?: CreateTodoOpts } | null;
}

export const useTodoMutations = (options: UseTodoMutationsOptions) => {
  const { t } = useI18n();
  const { registerUndo } = useUndo();
  const [newTodoText, setNewTodoText] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [newlyAddedIds, setNewlyAddedIds] = createSignal<Set<string>>(new Set());
  const [exitingIds, setExitingIds] = createSignal<Set<string>>(new Set());
  const [todoToDelete, setTodoToDelete] = createSignal<Todo | null>(null);

  const handleAddTodo = async (e: Event) => {
    e.preventDefault();
    const text = newTodoText().trim();
    const dbInstance = options.drizzle();
    const uid = options.userId();
    if (!text || !uid || !dbInstance || isSubmitting()) {
      return;
    }
    setIsSubmitting(true);
    try {
      // Apply the deterministic quick-add parser (dates / #tag / @list) when a
      // resolver is provided, merging its options over the per-view defaults so
      // an inline list/due date wins over the list the view is scoped to.
      const parsed = options.resolveQuickAdd?.(text);
      const finalText = parsed?.text || text;
      const finalOpts: CreateTodoOpts = {
        ...options.defaultAddTodoProps?.(),
        ...parsed?.opts,
      };
      const result = await createTodoMutation(dbInstance, uid, finalText, finalOpts);
      if (result) {
        // L1: undoing a just-created task removes the todo (there are not yet
        // subtasks or local history to preserve).
        registerUndo(t(msg`Task created`), () => purgeTodoMutation(dbInstance, result));
        setNewlyAddedIds((prev) => new Set([...prev, result]));
        setTimeout(() => {
          setNewlyAddedIds((prev) => {
            const next = new Set(prev);
            next.delete(result);
            return next;
          });
        }, NEW_ITEM_ANIMATION_DURATION_MS);
      }
      setNewTodoText("");
    } catch (err: unknown) {
      console.error("Error creating todo:", err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        options.todoInput()?.focus();
      }, 0);
    }
  };

  const handleToggleTodo = async (todo: Todo) => {
    const dbInstance = options.drizzle();
    if (dbInstance) {
      const wasCompleted = todo.completed === 1;
      try {
        await toggleTodoMutation(dbInstance, todo.id, wasCompleted, todo);
        // Completing a recurring task creates the next occurrence: simple undo
        // would lie there (it would leave the new occurrence), so omit it.
        const spawnsNextOccurrence = !wasCompleted && todo.recurrence_rule && todo.due_date;
        if (!spawnsNextOccurrence) {
          registerUndo(t(wasCompleted ? msg`Task reopened` : msg`Task completed`), () =>
            toggleTodoMutation(dbInstance, todo.id, !wasCompleted),
          );
        }
      } catch (err: unknown) {
        console.error("Error toggling todo:", err instanceof Error ? err.message : String(err));
      }
    }
  };

  const handleDeleteTodo = (id: string) => {
    const todo = options.currentListTodos()?.find((item) => item.id === id);
    if (todo) {
      setTodoToDelete(todo);
    }
  };

  const executeDeleteTodo = (id: string) => {
    const dbInstance = options.drizzle();
    if (!dbInstance || exitingIds().has(id)) {
      return;
    }

    setExitingIds((prev) => new Set([...prev, id]));

    setTimeout(() => {
      void (async () => {
        try {
          await deleteTodoMutation(dbInstance, id);
          registerUndo(t(msg`Task moved to trash`), () => restoreTodoMutation(dbInstance, id));
        } catch (err: unknown) {
          console.error("Error deleting todo:", err instanceof Error ? err.message : String(err));
        } finally {
          setExitingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      })();
    }, EXIT_DURATION_MS);
  };

  return {
    newTodoText,
    setNewTodoText,
    isSubmitting,
    newlyAddedIds,
    exitingIds,
    setExitingIds,
    todoToDelete,
    setTodoToDelete,
    handleAddTodo,
    handleToggleTodo,
    handleDeleteTodo,
    executeDeleteTodo,
  };
};
